import { useCallback, useEffect, useState } from "react";
import { Search, MoreVertical, Pencil, Ban, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, KIND_LABEL, qty, rp, shiftDate, todayJkt } from "@/lib/api";
import { EmptyState, Field, Loading, QtyInput, RupiahInput, SimBadge, StatusBadge } from "@/components/common";

const SIGN = { income: 1, cust_payment: 1, expense: -1, sup_payment: -1 };
const EDITABLE = {
  income: ["date", "qty", "unit", "amount", "note"], expense: ["date", "qty", "amount", "note"], usage: ["date", "qty", "note"],
  sup_credit: ["party", "date", "qty", "amount", "note"], cust_credit: ["party", "date", "qty", "amount", "note"],
  sup_payment: ["date", "amount", "note"], cust_payment: ["date", "amount", "note"],
};

export function describe(t, mats = {}) {
  const mname = mats[t.material]?.name || t.item_name;
  const q = t.qty ? `${qty(t.qty)} ${t.unit || ""}` : "";
  switch (t.kind) {
    case "income": return { title: t.product, sub: q };
    case "cust_payment": return { title: `Terima bayar kasbon — ${t.party}`, sub: "Otomatis dari Kasbon Pelanggan" };
    case "expense": return { title: t.category, sub: mname && t.material ? `${mname} ${q}` : "" };
    case "sup_payment": return { title: `Bayar kasbon — ${t.party}`, sub: "Otomatis dari Kasbon Pabrik" };
    case "sup_credit": return { title: `Kasbon — ${t.party}`, sub: [mname, q].filter(Boolean).join(" · ") };
    case "cust_credit": return { title: `Kasbon — ${t.party}`, sub: [t.product, q].filter(Boolean).join(" · ") };
    case "usage": return { title: `Pemakaian ${mname || ""}`, sub: q };
    case "opening_stock": return { title: `Stok awal ${mname || ""}`, sub: q };
    default: return { title: KIND_LABEL[t.kind], sub: "" };
  }
}

const amountText = (t) => {
  if (t.kind === "usage") return `-${qty(t.qty)} ${t.unit}`;
  if (t.kind === "opening_stock") return `+${qty(t.qty)} ${t.unit}`;
  const s = SIGN[t.kind];
  return s ? `${s > 0 ? "+" : "-"}${rp(t.amount)}` : rp(t.amount);
};
const amountColor = (t) => (t.kind === "usage" || SIGN[t.kind] < 0 ? "text-[#c0392b]" : SIGN[t.kind] > 0 ? "text-[#1f7a46]" : "text-[#1d4ed8]");

function EditDialog({ tx, onClose, onSaved }) {
  const { ws } = useAuth();
  const fields = EDITABLE[tx?.kind] || [];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (tx) setF({ date: tx.date, qty: String(tx.qty ?? ""), unit: tx.unit, amount: tx.amount, note: tx.note || "", party: tx.party || "" }); }, [tx]);
  const showQty = fields.includes("qty") && (tx?.kind !== "expense" || tx?.material) && !(tx?.kind?.endsWith("credit") && !tx?.qty && !tx?.material);
  const save = async () => {
    const body = {};
    fields.forEach((k) => { if (k === "qty") { if (showQty) body.qty = Number(f.qty) || 0; } else body[k] = f[k]; });
    setBusy(true);
    try { await api.patch(`/transactions/${tx.id}`, body); toast.success("Perubahan tersimpan."); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[var(--jt-ivory)]" data-testid="edit-dialog">
        <DialogHeader><DialogTitle>Ubah {KIND_LABEL[tx?.kind]}</DialogTitle><DialogDescription>Saldo, kasbon, dan stok akan dihitung ulang otomatis.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {fields.includes("party") && <Field label="Nama"><input className="jt-input" value={f.party} onChange={(e) => setF({ ...f, party: e.target.value })} data-testid="edit-party-input" /></Field>}
          <Field label="Tanggal"><input type="date" className="jt-input" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="edit-date-input" /></Field>
          {showQty && <div className="grid grid-cols-2 gap-3">
            <Field label="Jumlah"><QtyInput value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="edit-qty-input" /></Field>
            <Field label="Satuan">{fields.includes("unit")
              ? <select className="jt-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="edit-unit-select">{ws.product_units.map((u) => <option key={u}>{u}</option>)}</select>
              : <input className="jt-input" value={f.unit || ""} disabled />}</Field>
          </div>}
          {fields.includes("amount") && <Field label="Nominal"><RupiahInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="edit-amount-input" /></Field>}
          <Field label="Keterangan"><input className="jt-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="edit-note-input" /></Field>
          <button className="jt-btn jt-btn-primary w-full" disabled={busy} onClick={save} data-testid="edit-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Perubahan</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ tx, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/transactions/${tx.id}/cancel`, { reason }); toast.success("Transaksi dibatalkan. Saldo dan stok sudah disesuaikan."); onSaved(); onClose(); setReason(""); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[var(--jt-ivory)]" data-testid="cancel-dialog">
        <DialogHeader><DialogTitle>Batalkan transaksi ini?</DialogTitle>
          <DialogDescription>Transaksi tetap tersimpan sebagai riwayat dengan tanda “Dibatalkan”, tetapi tidak lagi dihitung di Uang Kas, kasbon, stok, dan laporan.</DialogDescription></DialogHeader>
        <Field label="Alasan (opsional)"><input className="jt-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: salah catat" data-testid="cancel-reason-input" /></Field>
        <div className="flex gap-2 justify-end">
          <button className="jt-btn jt-btn-ghost" onClick={onClose} data-testid="cancel-dialog-back">Kembali</button>
          <button className="jt-btn bg-[#c0392b] text-white hover:bg-[#a93226]" disabled={busy} onClick={go} data-testid="cancel-dialog-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Ya, Batalkan</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ tx, onClose, mats }) {
  if (!tx) return null;
  const d = describe(tx, mats);
  const rows = [["Jenis", KIND_LABEL[tx.kind]], ["Tanggal", fmtDate(tx.date)], ["Rincian", [d.title, d.sub].filter(Boolean).join(" · ")],
    ["Nominal", tx.kind === "usage" ? "-" : rp(tx.amount)], ["Keterangan", tx.note || "-"], ["Status", tx.status === "active" ? "Aktif" : `Dibatalkan${tx.cancel_reason ? ` (${tx.cancel_reason})` : ""}`],
    ["Sumber data", tx.is_simulation ? "Data contoh (simulasi)" : "Catatan asli"], ["Riwayat perubahan", `${tx.audit?.length || 0} kali`]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[var(--jt-ivory)]" data-testid="detail-dialog">
        <DialogHeader><DialogTitle>Detail Transaksi</DialogTitle><DialogDescription>Informasi lengkap catatan ini.</DialogDescription></DialogHeader>
        <dl className="divide-y text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-2"><dt className="text-muted-foreground">{k}</dt><dd className="font-semibold text-right">{v}</dd></div>)}</dl>
      </DialogContent>
    </Dialog>
  );
}

export function TxRow({ t, mats, onAction, testid }) {
  const d = describe(t, mats);
  const off = t.status !== "active";
  return (
    <div className={`flex items-center gap-3 py-3 ${off ? "opacity-55" : ""}`} data-testid={testid}>
      <div className="w-14 shrink-0 text-xs text-muted-foreground leading-tight">{fmtDate(t.date, { day: "2-digit", month: "short" })}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`font-bold text-sm sm:text-[15px] ${off ? "line-through" : ""}`}>{d.title}</span>
          {t.is_simulation && <SimBadge />}
          {off && <StatusBadge status="Dibatalkan" />}
        </div>
        <div className="text-xs sm:text-[13px] text-muted-foreground truncate">{[d.sub, t.note].filter(Boolean).join(" · ") || "—"}</div>
      </div>
      <div className={`text-sm sm:text-[15px] font-extrabold whitespace-nowrap ${amountColor(t)}`}>{amountText(t)}</div>
      {onAction && t.status === "active" && EDITABLE[t.kind] ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-black/5 shrink-0" aria-label="Aksi" data-testid={`${testid}-menu`}><MoreVertical className="w-4 h-4" /></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction("detail", t)} data-testid="tx-action-detail"><Info className="w-4 h-4 mr-2" /> Detail</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("edit", t)} data-testid="tx-action-edit"><Pencil className="w-4 h-4 mr-2" /> Ubah</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("cancel", t)} className="text-red-700" data-testid="tx-action-cancel"><Ban className="w-4 h-4 mr-2" /> Batalkan</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : onAction ? <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-black/5 shrink-0" aria-label="Detail" onClick={() => onAction("detail", t)} data-testid={`${testid}-detail`}><Info className="w-4 h-4" /></button> : null}
    </div>
  );
}

export function TxActionsHost({ state, setState, onChanged, mats }) {
  const close = () => setState(null);
  return (
    <>
      <EditDialog tx={state?.type === "edit" ? state.t : null} onClose={close} onSaved={onChanged} />
      <CancelDialog tx={state?.type === "cancel" ? state.t : null} onClose={close} onSaved={onChanged} />
      {state?.type === "detail" && <DetailDialog tx={state.t} onClose={close} mats={mats} />}
    </>
  );
}

export function History({ kinds, material, reloadKey, onChanged, testid = "history", emptyText = "Belum ada transaksi pada rentang tanggal ini." }) {
  const { ws, version } = useAuth();
  const mats = Object.fromEntries((ws?.materials || []).map((m) => [m.key, m]));
  const [start, setStart] = useState(shiftDate(todayJkt(), -30));
  const [end, setEnd] = useState(todayJkt());
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [act, setAct] = useState(null);
  const load = useCallback(() => {
    api.get("/transactions", { params: { kinds, start, end, q: q || undefined, material } })
      .then(({ data }) => setItems(data.transactions)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, [kinds, start, end, q, material]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load, reloadKey, version]);
  const changed = () => { load(); onChanged?.(); };
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1.4fr] gap-2 mb-2">
        <input type="date" className="jt-input" value={start} max={end} onChange={(e) => setStart(e.target.value)} aria-label="Dari tanggal" data-testid={`${testid}-start`} />
        <input type="date" className="jt-input" value={end} min={start} onChange={(e) => setEnd(e.target.value)} aria-label="Sampai tanggal" data-testid={`${testid}-end`} />
        <div className="relative col-span-2 sm:col-span-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="jt-input pl-9" placeholder="Cari keterangan atau nama..." value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
        </div>
      </div>
      {items === null ? <Loading /> : items.length === 0 ? <EmptyState text={q ? "Tidak ada hasil yang cocok dengan pencarian." : emptyText} testid={`${testid}-empty`} /> : (
        <div className="divide-y divide-[var(--jt-gold)]/15">
          {items.map((t) => <TxRow key={t.id} t={t} mats={mats} testid={`${testid}-row`} onAction={(type, tx) => setAct({ type, t: tx })} />)}
        </div>
      )}
      <TxActionsHost state={act} setState={setAct} onChanged={changed} mats={mats} />
    </div>
  );
}
